/**
 * Application routes
 * Language-based routing with /:locale prefix for all public routes
 * Role-based routing with protected routes
 * Lazy-loaded for optimal code splitting
 * @module app/routes
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import Spinner from '../components/ui/Spinner';

// Eagerly loaded (critical path)
import Home from '../pages/Home';
import NotFound from '../pages/NotFound';

// Lazy-loaded public pages
const ProductsPage = lazy(() => import('../pages/ProductsPage'));
const ProductPage = lazy(() => import('../pages/ProductPage'));
const Login = lazy(() => import('../pages/Login'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const Register = lazy(() => import('../pages/Register'));
const Cart = lazy(() => import('../pages/Cart'));
const Checkout = lazy(() => import('../pages/Checkout'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const OrderHistory = lazy(() => import('../pages/OrderHistory'));
const OrderDetail = lazy(() => import('../pages/OrderDetail'));
const BookingsPage = lazy(() => import('../features/dashboard/bookings/pages/BookingsPage'));
const InquiriesPage = lazy(() => import('../features/dashboard/inquiries/pages/InquiriesPage'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('../pages/TermsConditions'));
const DynamicPage = lazy(() => import('../pages/DynamicPage'));
const GetQuote = lazy(() => import('../pages/GetQuote'));
const ApplicationPage = lazy(() => import('../pages/ApplicationPage'));
const ProductApplicationsPage = lazy(() => import('../pages/ProductApplicationsPage'));
const ProductCountryPage = lazy(() => import('../pages/ProductCountryPage'));
const ApplicationCountryPage = lazy(() => import('../pages/ApplicationCountryPage'));
const ContactUs = lazy(() => import('../pages/ContactUs'));
const PartnerProgram = lazy(() => import('../pages/PartnerProgram'));
const ProductCategoryPage = lazy(() => import('../pages/ProductCategoryPage'));
const MvpPackagesPage = lazy(() => import('../mvp/features/public/pages/PackagesPage'));
const MvpPackageDetailPage = lazy(() => import('../mvp/features/public/pages/PackageDetailPage'));
const MvpDestinationsPage = lazy(() => import('../mvp/features/public/pages/DestinationsPage'));
const MvpDestinationDetailPage = lazy(() => import('../mvp/features/public/pages/DestinationDetailPage'));
const BlogPage = lazy(() => import('../pages/ArticlesPage'));
const ArticlePage = lazy(() => import('../pages/ArticlePage'));
const MvpInquiryPage = lazy(() => import('../mvp/features/public/pages/InquiryPage'));

// Lazy-loaded admin pages (heavy, rarely used by most users)
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const AdminOrders = lazy(() => import('../pages/admin/AdminOrders'));
const AdminUsers = lazy(() => import('../pages/admin/AdminUsers'));
const AdminAuditLogs = lazy(() => import('../pages/admin/AdminAuditLogs'));
const AdminAnalytics = lazy(() => import('../pages/admin/AdminAnalytics'));
const AdminPages = lazy(() => import('../pages/admin/AdminPages'));
const AdminPageEditor = lazy(() => import('../pages/admin/AdminPageEditor'));
const AdminDocuments = lazy(() => import('../pages/admin/AdminDocuments'));
const AdminDownloadLogs = lazy(() => import('../pages/admin/AdminDownloadLogs'));
const AdminHero = lazy(() => import('../pages/admin/AdminHero'));
const AdminArticles = lazy(() => import('../pages/admin/AdminArticles'));
const AdminArticleEditor = lazy(() => import('../features/articles/pages/ArticleEditorPage'));
const AdminArticleAnalytics = lazy(() => import('../pages/admin/AdminArticleAnalytics'));
const AdminHomeSections = lazy(() => import('../pages/admin/AdminHomeSections'));
const HomepageSectionPage = lazy(() => import('../pages/HomepageSectionPage'));
const AdminNavMenu = lazy(() => import('../pages/admin/AdminNavMenu'));
const AdminFooter  = lazy(() => import('../pages/admin/AdminFooter'));
const AdminSiteSettings = lazy(() => import('../pages/admin/AdminSiteSettings'));
const ChatFunnelManager = lazy(() => import('../pages/admin/ChatFunnelManager'));
const AdminSEO       = lazy(() => import('../pages/admin/AdminSEO'));
const AdminRedirects = lazy(() => import('../pages/admin/AdminRedirects'));
const AdminTestimonials = lazy(() => import('../pages/admin/AdminTestimonials'));
const AdminBranding  = lazy(() => import('../pages/admin/AdminBranding'));
const AdminApplications = lazy(() => import('../pages/admin/AdminApplications'));
const AdminProductCategories = lazy(() => import('../pages/admin/AdminProductCategories'));
const AdminTemplateEditor    = lazy(() => import('../pages/admin/AdminTemplateEditor'));
const AdminTranslationDashboard = lazy(() => import('../pages/admin/AdminTranslationDashboard'));
const AdminTranslationWorkflow = lazy(() => import('../pages/admin/AdminTranslationWorkflow'));
const AdminMaintenance = lazy(() => import('../pages/admin/AdminMaintenance'));
const MvpAdminDashboardPage = lazy(() => import('../mvp/features/admin/pages/AdminDashboardPage'));
const MvpAdminPackagesPage = lazy(() => import('../mvp/features/admin/pages/AdminPackagesPage'));
const MvpAdminPackageNewPage = lazy(() => import('../mvp/features/admin/pages/AdminPackageNewPage'));
const MvpAdminPackageEditPage = lazy(() => import('../mvp/features/admin/pages/AdminPackageEditPage'));
const MvpAdminActivitiesPage = lazy(() => import('../mvp/features/admin/pages/AdminActivitiesPage'));
const MvpAdminDestinationsPage = lazy(() => import('../mvp/features/admin/pages/AdminDestinationsPage'));
const MvpAdminDestinationNewPage = lazy(() => import('../mvp/features/admin/pages/AdminDestinationNewPage'));
const MvpAdminDestinationEditPage = lazy(() => import('../mvp/features/admin/pages/AdminDestinationEditPage'));
const MvpAdminInquiriesPage = lazy(() => import('../mvp/features/admin/pages/AdminInquiriesPage'));
const MvpAdminInquiryDetailPage = lazy(() => import('../mvp/features/admin/pages/AdminInquiryDetailPage'));
const MvpAdminBookingsPage = lazy(() => import('../mvp/features/admin/pages/AdminBookingsPage'));
const MvpAdminBookingEditPage = lazy(() => import('../mvp/features/admin/pages/AdminBookingEditPage'));
const MvpAdminTestimonialsPage = lazy(() => import('../mvp/features/admin/pages/AdminTestimonialsPage'));
const MvpAdminBlogPage = lazy(() => import('../mvp/features/admin/pages/AdminBlogPage'));
const MvpAdminBlogNewPage = lazy(() => import('../mvp/features/admin/pages/AdminBlogNewPage'));
const MvpAdminBlogEditPage = lazy(() => import('../mvp/features/admin/pages/AdminBlogEditPage'));
const MvpAdminSettingsPage = lazy(() => import('../mvp/features/admin/pages/AdminSettingsPage'));

// SEO Dashboard pages (enterprise SEO engine)
const SeoOverview      = lazy(() => import('../features/seo-dashboard/pages/SeoOverview'));
const MetadataManager  = lazy(() => import('../features/seo-dashboard/pages/MetadataManager'));
const SchemaManager    = lazy(() => import('../features/seo-dashboard/pages/SchemaManager'));
const ContentAnalyzer  = lazy(() => import('../features/seo-dashboard/pages/ContentAnalyzer'));
const RedirectManager  = lazy(() => import('../features/seo-dashboard/pages/RedirectManager'));
const InternalLinks    = lazy(() => import('../features/seo-dashboard/pages/InternalLinks'));
const TechnicalSEO     = lazy(() => import('../features/seo-dashboard/pages/TechnicalSEO'));
const SitemapManager   = lazy(() => import('../features/seo-dashboard/pages/SitemapManager'));
const IndexingManager  = lazy(() => import('../features/seo-dashboard/pages/IndexingManager'));
const SeoAnalytics     = lazy(() => import('../features/seo-dashboard/pages/SeoAnalytics'));

// Public About page
const About = lazy(() => import('../features/pages/aboutUs'));
const SourceDownloads = lazy(() => import('../pages/SourceDownloads'));

// Protected route components
import ProtectedRoute from '../components/protected/ProtectedRoute';
import GuestRoute from '../components/protected/GuestRoute';
import AdminRoute from '../components/protected/AdminRoute';
import DeveloperRoute from '../components/protected/DeveloperRoute';
import LanguageRouter from '../components/i18n/LanguageRouter';
import GeoRedirect from '../components/i18n/GeoRedirect';
import LanguageRedirect from '../components/routing/LanguageRedirect';

const AdminLayout = lazy(() => import('../components/layout/AdminLayout'));

// Suspense fallback
const SuspenseFallback = () => (
  <div className="flex justify-center items-center min-h-[60vh]">
    <Spinner size="lg" />
  </div>
);

const AdminProductEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/admin/tour-packages/${id}/edit`} replace />;
};

const AdminTourCategoriesRedirect = () => <Navigate to="/admin/tour-categories" replace />;
const AdminActivitiesRedirect = () => <Navigate to="/admin/activities" replace />;

const LegacyMvpBlogRedirect = () => {
  const { lang = 'en' } = useParams();
  return <Navigate to={`/${lang}/blog`} replace />;
};

const LegacyMvpBlogPostRedirect = () => {
  const { lang = 'en', slug } = useParams();
  return <Navigate to={`/${lang}/blog/${slug}`} replace />;
};

const LegacyMvpPackagesRedirect = () => {
  const { lang = 'en' } = useParams();
  return <Navigate to={`/${lang}/packages`} replace />;
};

const LegacyMvpPackageDetailRedirect = () => {
  const { lang = 'en', slug } = useParams();
  return <Navigate to={`/${lang}/packages/${slug}`} replace />;
};



const AppRoutes = () => {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>
        {/* Root: Geo-based language redirect for first-time visitors */}
        <Route path="/" element={<GeoRedirect />} />
        <Route path="/login" element={<LanguageRedirect />} />
        <Route path="/forgot-password" element={<LanguageRedirect />} />
        <Route path="/reset-password/*" element={<LanguageRedirect />} />
        <Route path="/register" element={<LanguageRedirect />} />
        <Route path="/dashboard" element={<LanguageRedirect />} />
        <Route path="/dashboard/*" element={<LanguageRedirect />} />
        <Route path="/products/*" element={<LanguageRedirect />} />
        <Route path="/packages/*" element={<LanguageRedirect />} />
        <Route path="/blog/*" element={<LanguageRedirect />} />
        <Route path="/destinations/*" element={<LanguageRedirect />} />
        <Route path="/categories/*" element={<LanguageRedirect />} />
        <Route path="/privacy" element={<LanguageRedirect />} />
        <Route path="/terms" element={<LanguageRedirect />} />
        <Route path="/about" element={<LanguageRedirect />} />
        <Route path="/about-us" element={<LanguageRedirect />} />
        <Route path="/contact" element={<LanguageRedirect />} />
        <Route path="/contact-us" element={<LanguageRedirect />} />
        <Route path="/get-quote" element={<LanguageRedirect />} />
        <Route path="/inquiry" element={<LanguageRedirect />} />
        <Route path="/partner-program" element={<LanguageRedirect />} />
        <Route path="/bookings/new" element={<LanguageRedirect />} />
        <Route path="/cart" element={<LanguageRedirect />} />
        <Route path="/checkout" element={<LanguageRedirect />} />
        <Route path="/orders" element={<LanguageRedirect />} />
        <Route path="/orders/*" element={<LanguageRedirect />} />
        <Route path="/homepage-sections/:sectionKey" element={<HomepageSectionPage />} />

        {/* Language-scoped routes */}
        <Route path="/:locale" element={<LanguageRouter />}>
          {/* Public Routes */}
          <Route index element={<Home />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="packages" element={<MvpPackagesPage />} />
          <Route path="products/:slug" element={<ProductPage />} />
          <Route path="packages/:slug" element={<MvpPackageDetailPage />} />
          <Route path="products/:slug/application" element={<ProductApplicationsPage />} />
          <Route path="packages/:slug/application" element={<ProductApplicationsPage />} />
          <Route path="products/:slug/application/:applicationSlug" element={<ApplicationPage />} />
          <Route path="packages/:slug/application/:applicationSlug" element={<ApplicationPage />} />
          {/* Programmatic SEO: country-specific pages — static "application" segment always wins above */}
          <Route path="products/:slug/:country" element={<ProductCountryPage />} />
          <Route path="packages/:slug/:country" element={<ProductCountryPage />} />
          <Route path="products/:slug/application/:applicationSlug/:country" element={<ApplicationCountryPage />} />
          <Route path="packages/:slug/application/:applicationSlug/:country" element={<ApplicationCountryPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<ArticlePage />} />
          <Route path="homepage-sections/:sectionKey" element={<HomepageSectionPage />} />
          <Route element={<GuestRoute />}>
            <Route path="login" element={<Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password/:token" element={<ResetPassword />} />
            <Route path="register" element={<Register />} />
          </Route>
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsConditions />} />
          <Route path="about" element={<About />} />
          <Route path="about-us" element={<About />} />
          <Route path="contact" element={<ContactUs />} />
          <Route path="contact-us" element={<ContactUs />} />
          <Route path="partner-program" element={<PartnerProgram />} />
          <Route path="categories/:slug" element={<ProductCategoryPage />} />

          {/* Get Quote — public, no auth required */}
          <Route path="get-quote" element={<GetQuote />} />
          <Route path="inquiry" element={<GetQuote />} />
          <Route path="bookings/new" element={<GetQuote />} />
          <Route path="mvp/inquiry" element={<MvpInquiryPage />} />
          <Route path="mvp/blog" element={<LegacyMvpBlogRedirect />} />
          <Route path="mvp/blog/:slug" element={<LegacyMvpBlogPostRedirect />} />
          <Route path="mvp/packages" element={<LegacyMvpPackagesRedirect />} />
          <Route path="mvp/packages/:slug" element={<LegacyMvpPackageDetailRedirect />} />
          <Route path="mvp/*" element={<Navigate to=".." replace relative="path" />} />

          {/* Main MVP public pages */}
          <Route path="destinations" element={<MvpDestinationsPage />} />
          <Route path="destinations/:slug" element={<MvpDestinationDetailPage />} />
          <Route path="mvp/destinations" element={<MvpDestinationsPage />} />
          <Route path="mvp/destinations/:slug" element={<MvpDestinationDetailPage />} />
          <Route path="packages/inquiry" element={<MvpInquiryPage />} />

          {/* Temporary legacy fallback namespace */}
          <Route path="old/*" element={<Home />} />

          {/* Dynamic CMS Pages — catch-all for admin-created pages (static routes above take precedence) */}
          <Route path=":slug" element={<DynamicPage />} />

          {/* Protected Routes (authenticated users) */}
          <Route element={<ProtectedRoute />}>
            <Route path="cart" element={<Cart />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="dashboard/bookings" element={<BookingsPage />} />
            <Route path="dashboard/inquiries" element={<InquiriesPage />} />
            <Route path="dashboard/saved-journeys" element={<MvpDestinationsPage />} />
            <Route path="dashboard/settings/*" element={<Dashboard />} />
            <Route path="orders" element={<OrderHistory />} />
            <Route path="orders/:id" element={<OrderDetail />} />
          </Route>
        </Route>

        {/* Admin Routes — NOT language-scoped (internal-only) */}
        <Route element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/products" element={<Navigate to="/admin/tour-packages" replace />} />
          <Route path="/admin/tour-packages" element={<MvpAdminPackagesPage />} />
          <Route path="/admin/products/new" element={<Navigate to="/admin/tour-packages/new" replace />} />
          <Route path="/admin/tour-packages/new" element={<MvpAdminPackageNewPage />} />
          <Route path="/admin/products/:id/edit" element={<AdminProductEditRedirect />} />
          <Route path="/admin/tour-packages/:id/edit" element={<MvpAdminPackageEditPage />} />
          <Route path="/admin/applications" element={<AdminApplications />} />
          <Route path="/admin/activities" element={<MvpAdminActivitiesPage />} />
          <Route path="/admin/product-categories" element={<AdminTourCategoriesRedirect />} />
          <Route path="/admin/tour-categories" element={<AdminProductCategories />} />
          <Route path="/admin/content-templates/application-description" element={<AdminTemplateEditor />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/bookings" element={<MvpAdminBookingsPage />} />
          <Route path="/admin/bookings/:id/edit" element={<MvpAdminBookingEditPage />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/audit" element={<AdminAuditLogs />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/inquiries" element={<MvpAdminInquiriesPage />} />
          <Route path="/admin/inquiries/:id" element={<MvpAdminInquiryDetailPage />} />
          <Route path="/admin/pages" element={<AdminPages />} />
          <Route path="/admin/pages/new" element={<AdminPageEditor />} />
          <Route path="/admin/pages/:id/edit" element={<AdminPageEditor />} />
          <Route path="/admin/destinations" element={<MvpAdminDestinationsPage />} />
          <Route path="/admin/destinations/new" element={<MvpAdminDestinationNewPage />} />
          <Route path="/admin/destinations/:id/edit" element={<MvpAdminDestinationEditPage />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin/articles" element={<AdminArticles />} />
          <Route path="/admin/articles/new" element={<AdminArticleEditor />} />
          <Route path="/admin/articles/:id/edit" element={<AdminArticleEditor />} />
          <Route path="/admin/articles/analytics" element={<AdminArticleAnalytics />} />
          <Route path="/admin/documents" element={<AdminDocuments />} />
          <Route path="/admin/download-logs" element={<AdminDownloadLogs />} />
          <Route path="/admin/hero" element={<AdminHero />} />
          <Route path="/admin/home-sections" element={<AdminHomeSections />} />
          <Route path="/admin/navigation" element={<AdminNavMenu />} />
          <Route path="/admin/footer" element={<AdminFooter />} />
          <Route path="/admin/site-settings" element={<AdminSiteSettings />} />
          <Route path="/admin/chatfunnel" element={<ChatFunnelManager />} />
          <Route path="/admin/seo" element={<AdminSEO />} />
          <Route path="/admin/redirects" element={<AdminRedirects />} />

          {/* Enterprise SEO Dashboard routes */}
          <Route path="/admin/seo/overview"   element={<SeoOverview />} />
          <Route path="/admin/seo/metadata"   element={<MetadataManager />} />
          <Route path="/admin/seo/schema"     element={<SchemaManager />} />
          <Route path="/admin/seo/analyzer"   element={<ContentAnalyzer />} />
          <Route path="/admin/seo/redirects"  element={<RedirectManager />} />
          <Route path="/admin/seo/links"      element={<InternalLinks />} />
          <Route path="/admin/seo/technical"  element={<TechnicalSEO />} />
          <Route path="/admin/seo/sitemap"    element={<SitemapManager />} />
          <Route path="/admin/seo/indexing"   element={<IndexingManager />} />
          <Route path="/admin/seo/analytics"  element={<SeoAnalytics />} />
          <Route path="/admin/translation-workflow" element={<AdminTranslationWorkflow />} />

          {/* Developer-only platform and MVP routes */}
          <Route element={<DeveloperRoute />}>
            <Route path="/admin/branding" element={<AdminBranding />} />
            <Route path="/admin/maintenance" element={<AdminMaintenance />} />
            <Route path="/admin/mvp" element={<MvpAdminDashboardPage />} />
            <Route path="/admin/mvp/packages" element={<MvpAdminPackagesPage />} />
            <Route path="/admin/mvp/packages/new" element={<MvpAdminPackageNewPage />} />
            <Route path="/admin/mvp/packages/:id/edit" element={<MvpAdminPackageEditPage />} />
            <Route path="/admin/mvp/activities" element={<AdminActivitiesRedirect />} />
            <Route path="/admin/mvp/destinations" element={<MvpAdminDestinationsPage />} />
            <Route path="/admin/mvp/destinations/new" element={<MvpAdminDestinationNewPage />} />
            <Route path="/admin/mvp/destinations/:id/edit" element={<MvpAdminDestinationEditPage />} />
            <Route path="/admin/mvp/inquiries" element={<MvpAdminInquiriesPage />} />
            <Route path="/admin/mvp/inquiries/:id" element={<MvpAdminInquiryDetailPage />} />
            <Route path="/admin/mvp/bookings" element={<Navigate to="/admin/bookings" replace />} />
            <Route path="/admin/mvp/bookings/:id/edit" element={<Navigate to="/admin/bookings/:id/edit" replace />} />
            <Route path="/admin/mvp/testimonials" element={<MvpAdminTestimonialsPage />} />
            <Route path="/admin/mvp/blog" element={<MvpAdminBlogPage />} />
            <Route path="/admin/mvp/blog/new" element={<MvpAdminBlogNewPage />} />
            <Route path="/admin/mvp/blog/:id/edit" element={<MvpAdminBlogEditPage />} />
            <Route path="/admin/mvp/settings" element={<MvpAdminSettingsPage />} />
          </Route>

          {/* Translation dashboard (includes approval tab) */}
          <Route path="/admin/translation-dashboard" element={<AdminTranslationDashboard />} />

          </Route>{/* end AdminLayout */}
        </Route>

        {/* Public downloads page */}
        <Route path="/downloads" element={<SourceDownloads />} />
        <Route path="/:slug" element={<LanguageRedirect />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;

// Developed and Designed by Trophy Developers
